import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, OverlayTrigger, Tooltip, Table } from "react-bootstrap";
import { EmptyList, Pagination, Spinner, WithRoleAccess } from "@services/ui-components";
import { useReactiveVar } from "@apollo/client";
import RenderProductCategoryName from "../../WarehousesGoods/components/RenderProductCategoryName";
import { Link, useParams } from "react-router-dom";
import AddProductListModal from "../AddProductListModal/AddProductListModal";
import { CompanyAccess, ProductProxy } from "@me-team/host/main/graphql/types";
import { useGetAddNewProductListQuery } from "@me-team/host/main/addNewProductList/graphql/addNewProductList.hooks";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { ProductsFilterI } from "../../Products/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import { LOCAL_STORAGE_CREATED_ID } from "../../Joyride/WarehouseTutorial/constants";
import { getItemsPerPageValue } from "../AddProductListModal/utils";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
interface ProductsTableProps {
    filters: {
        brand: string;
        categoryId: number;
        nameSkuBarcode: string;
    };
    handleModal: (i?: ProductProxy) => void;
    setName: any;
    showModal: boolean;
    onHide: () => void;
    refetch?: () => void;
    existingProduct: ProductProxy;
    setExistingProduct: (i?: any) => void;
    storageId?: number;
    initialValueFilter?: ProductsFilterI;
    onDuplicateProductModal?: (productId: number) => Promise<void>;
    setProductForChange: (product: ProductProxy) => void;
    productForChange: ProductProxy;
}
const AddNewProductListTable: React.FC<ProductsTableProps> = ({
    filters,
    handleModal,
    setName,
    showModal,
    existingProduct,
    setExistingProduct,
    initialValueFilter,
    onDuplicateProductModal,
    setProductForChange,
    productForChange,
}) => {
    const { t } = useTranslation();
    const { id, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canAddProduct = useRoleAccess(CompanyAccess.WarehouseProductAdd);
    const [currentPage, setCurrentPage] = useState(1);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const createdProductId = localStorage.getItem(LOCAL_STORAGE_CREATED_ID);
    const itemsPerPageFilter = getItemsPerPageValue(tutorialActive, createdProductId, tutorialType, startStep);

    const { data, loading, refetch } = useGetAddNewProductListQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPageFilter,
            branchesPaginatedId: +branchId,
            nameSkuBarcode: filters?.nameSkuBarcode === "" ? null : filters?.nameSkuBarcode,
            categoryId: filters?.categoryId,
            brand: filters?.brand,
            storagesPaginated: +id,
            companyId: +currentCompanyId,
            productId: tutorialActive && createdProductId ? Number(createdProductId) : null,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            setName(data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated?.storages[0]?.name || "");
        },
    });

    const outOfStockProducts =
        data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated?.storages[0]?.outOfStockProducts;
    const products = outOfStockProducts?.products;

    const pageCount = parseInt(outOfStockProducts?.pageCount || "0");
    const totalItems = outOfStockProducts?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPageFilter + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPageFilter, totalItems);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };
    if (loading || !currentCompanyId) return <Spinner />;
    return (
        <>
            <Table responsive>
                <thead className="fs-7">
                    <tr>
                        <th className="px-4"> {t("Product name")}</th>
                        <th>{t("ProductsList.Brand")}</th>
                        <th>{t("ProductsList.Category")}</th>
                        <th>{t("ProductsList.SKU")}</th>
                        <th>{t("ProductsList.Barcode")}</th>
                        <th>{t("ProductsList.Unit")}</th>
                        {canAddProduct ? <th></th> : null}
                    </tr>
                </thead>
                <tbody>
                    {products?.map((i) => (
                        <tr key={i?.id}>
                            <td className={`px-4 'text-primary'} align-middle`}>
                                <WithRoleAccess permission={CompanyAccess.ProductEdit} fallback={<span>{i?.name}</span>}>
                                    <Link
                                        to={`/warehouse/product/${currentCompanyId}/${i?.id}/edit-product/branch/${branchId}`}
                                        className={`text-decoration-none text-primary`}
                                    >
                                        {i?.name}
                                    </Link>
                                </WithRoleAccess>
                            </td>
                            <td className={` align-middle`}>{i?.brand}</td>
                            <td className={` align-middle`}>
                                <RenderProductCategoryName category={i?.productCategoryNew} isArchive={false} />
                            </td>
                            <td className={` align-middle`}>{i?.sku}</td>
                            <td className={` align-middle`}>{i?.barcode}</td>
                            <td className={` align-middle`}>
                                {i?.unit?.name} {i?.alternativeUnit?.name ? `(${i?.alternativeUnit?.name})` : null}
                            </td>
                            {canAddProduct ? (
                                <td>
                                    <div className="d-flex justify-content-end">
                                        <OverlayTrigger overlay={<Tooltip>{t("Add Product")}</Tooltip>}>
                                            <Button
                                                variant="outline-secondary"
                                                className="outline-secondary btn-size text-dark m-1 p-0"
                                                onClick={() => {
                                                    tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                                                    handleModal(i);
                                                }}
                                                style={{ height: "39px", width: "39px" }}
                                                data-tour="add-product"
                                            >
                                                <i className="bi bi-plus-lg"></i>
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </Table>
            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <EmptyList title={"No products were found for the specified values in the filters or search."} />
            )}
            <AddProductListModal
                show={showModal}
                onHide={() => ""}
                handleModal={handleModal}
                existingProduct={existingProduct}
                setExistingProduct={() => setExistingProduct(null)}
                storageId={+id}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
                onDuplicateProductModal={onDuplicateProductModal}
                setProductForChange={setProductForChange}
                productForChange={productForChange}
                dialogClassName={tutorialActive && "add-product-to-warehouse"}
                centered={tutorialActive && false}
                dataTour={"add-product-to-warehouse-form"}
            />

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default AddNewProductListTable;
